/**
 * Coordinate assignment — docs/08 §4.2 step 4.
 *
 * The ordering pass decides *who sits next to whom*; this decides where. Those
 * are different problems, and getting the first right without the second gives
 * you rows of boxes joined by long crawling lines — every card evenly spaced,
 * no child under its parents, and none of the shape a family chart is read for.
 *
 * The method is the usual one for layered graphs, specialised to genealogy:
 * alternate between pulling children under their parents and pulling parents
 * over their children, and after each pull, re-pack the layer so nothing
 * overlaps and the order the barycentre pass chose is preserved exactly.
 *
 * Two things move as **units**, and both matter more than the arithmetic:
 *
 *  - a **couple group** — a rail of a hub and their spouses is one household,
 *    and a pass that slid a wife away from her husband to satisfy her own
 *    children would take the chart apart to tidy it;
 *  - the **sibship** under one junction, by centring the junction on its
 *    children rather than each child on its parents, so brothers and sisters
 *    stay together under the marriage that produced them.
 */

import type { CoupleGroup } from './couples.js';
import type { OrderedLayer } from './order.js';
import type { LayoutOptions } from './types.js';

export interface CoordsInput {
  layers: OrderedLayer[];
  /** child id → the ids of its parents (drawable edges only). */
  parentsOf: Map<string, string[]>;
  opts: LayoutOptions;
  /** Refinement rounds. Four is plenty at family scale; the pass converges fast. */
  rounds?: number;
}

/** Left edge of every person, keyed by id. */
export type Positions = Map<string, number>;

const groupWidth = (group: CoupleGroup, opts: LayoutOptions): number =>
  group.members.length * opts.cardWidth + (group.members.length - 1) * opts.cardGap;

/** Where a member sits inside its own group, as an offset from the group's left edge. */
const memberOffset = (group: CoupleGroup, id: string, opts: LayoutOptions): number =>
  group.members.indexOf(id) * (opts.cardWidth + opts.cardGap);

export function assignCoordinates(input: CoordsInput): Positions {
  const { layers, parentsOf, opts } = input;
  const rounds = input.rounds ?? 4;
  // Households sit further apart than the people inside one: the gap is what
  // makes a rail read as a single family rather than a run of individuals.
  const groupGap = opts.cardGap * 2;

  /** Group left edges, per layer, in the layer's fixed order. */
  const placement = layers.map((layer) => {
    let x = 0;
    return layer.groups.map((group) => {
      const at = x;
      x += groupWidth(group, opts) + groupGap;
      return at;
    });
  });

  const centreOf = (layerIndex: number, groupIndex: number): number =>
    placement[layerIndex]![groupIndex]! +
    groupWidth(layers[layerIndex]!.groups[groupIndex]!, opts) / 2;

  const personX = (): Positions => {
    const positions: Positions = new Map();
    layers.forEach((layer, layerIndex) => {
      layer.groups.forEach((group, groupIndex) => {
        for (const member of group.members) {
          positions.set(
            member,
            placement[layerIndex]![groupIndex]! + memberOffset(group, member, opts),
          );
        }
      });
    });
    return positions;
  };

  const childrenOf = new Map<string, string[]>();
  for (const [child, parents] of parentsOf) {
    for (const parent of parents) {
      childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), child]);
    }
  }

  /**
   * Re-pack a layer around the positions it would like, keeping the order and
   * the minimum separation.
   *
   * Two sweeps, not one. A single left-to-right pass that only ever pushes right
   * makes the whole layer drift away from where it wanted to be, one collision
   * at a time; the return sweep gives back the slack, so a group that has room
   * on its left actually uses it.
   */
  const pack = (layerIndex: number, desired: Array<number | null>): void => {
    const groups = layers[layerIndex]!.groups;
    const widths = groups.map((group) => groupWidth(group, opts));
    const current = placement[layerIndex]!;

    const next = groups.map((_, index) => desired[index] ?? current[index]!);
    for (let i = 1; i < next.length; i += 1) {
      next[i] = Math.max(next[i]!, next[i - 1]! + widths[i - 1]! + groupGap);
    }
    for (let i = next.length - 2; i >= 0; i -= 1) {
      // Only ever move left, and never past what the left neighbour allows.
      const ceiling = next[i + 1]! - widths[i]! - groupGap;
      const floor = i === 0 ? -Infinity : next[i - 1]! + widths[i - 1]! + groupGap;
      next[i] = Math.max(Math.min(next[i]!, ceiling), Math.min(floor, next[i]!));
    }
    placement[layerIndex] = next;
  };

  for (let round = 0; round < rounds; round += 1) {
    /* Downward: a sibship settles under the marriage that produced it. */
    for (let layerIndex = 1; layerIndex < layers.length; layerIndex += 1) {
      const positions = personX();
      const desired = layers[layerIndex]!.groups.map((group) => {
        const wants: number[] = [];
        for (const member of group.members) {
          const parents = (parentsOf.get(member) ?? [])
            .map((parent) => positions.get(parent))
            .filter((value): value is number => value !== undefined);
          if (parents.length === 0) continue;
          const parentCentre =
            parents.reduce((sum, value) => sum + value + opts.cardWidth / 2, 0) / parents.length;
          // Where the group's left edge would have to be for this member to sit
          // under their parents.
          wants.push(parentCentre - opts.cardWidth / 2 - memberOffset(group, member, opts));
        }
        return wants.length > 0 ? wants.reduce((a, b) => a + b, 0) / wants.length : null;
      });
      pack(layerIndex, desired);
    }

    /* Upward: parents centre themselves over the children they share. */
    for (let layerIndex = layers.length - 2; layerIndex >= 0; layerIndex -= 1) {
      const positions = personX();
      const desired = layers[layerIndex]!.groups.map((group, groupIndex) => {
        const kids = new Set<string>();
        for (const member of group.members) {
          for (const child of childrenOf.get(member) ?? []) kids.add(child);
        }
        const centres = [...kids]
          .map((child) => positions.get(child))
          .filter((value): value is number => value !== undefined)
          .map((value) => value + opts.cardWidth / 2);
        if (centres.length === 0) return null;
        const mean = centres.reduce((a, b) => a + b, 0) / centres.length;
        return mean - (centreOf(layerIndex, groupIndex) - placement[layerIndex]![groupIndex]!);
      });
      pack(layerIndex, desired);
    }
  }

  // Normalise: the leftmost card in the whole chart sits at 0.
  const positions = personX();
  const left = Math.min(...positions.values());
  if (Number.isFinite(left) && left !== 0) {
    for (const [id, value] of positions) positions.set(id, value - left);
  }
  return positions;
}
